import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { Product } from '../types';

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        setLoading(false);
        console.warn("Products load timed out. Check connection.");
      }
    }, 10000); // 10 second timeout

    const q = query(collection(db, 'products'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isMounted) return;
      clearTimeout(timeoutId);
      const prods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      // Client-side sort to ensure all products show up even if 'order' field is missing
      const sortedProds = [...prods].sort((a, b) => {
        const orderA = a.order ?? 9999;
        const orderB = b.order ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
      });
      setProducts(sortedProds);
      setLoading(false);
    }, (error) => {
      if (!isMounted) return;
      clearTimeout(timeoutId);
      handleFirestoreError(error, OperationType.GET, 'products');
      setLoading(false);
    });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  return { products, loading };
}
